import React from 'react';
import { motion } from 'framer-motion';
import { ScrollButton } from './Shared';
type Bonus = {
  id: number;
  emoji: string;
  title: string;
  desc: string;
  bookBg: string;
  bookSpine: string;
};
const bonuses: Bonus[] = [{
  id: 1,
  emoji: '📋',
  title: 'El Planner de Sesiones 360',
  desc: 'Plantillas de planificación diaria, semanal y mensual listas para imprimir. Nunca más improvises qué dar el lunes por la mañana.',
  bookBg: 'bg-[#7C9070]',
  bookSpine: 'bg-[#5F7256]'
}, {
  id: 2,
  emoji: '🗣️',
  title: 'El Arte de los Cues',
  desc: '100+ frases sensoriales e imaginarias organizadas por zona corporal. Corrige a tus alumnos sin tocarlos y sin repetir siempre lo mismo.',
  bookBg: 'bg-[#C86B5E]',
  bookSpine: 'bg-[#A65347]'
}, {
  id: 3,
  emoji: '⚡',
  title: 'Pilates Express 20 Minutos',
  desc: '8 clases completas de 20 minutos para el alumno que dice que no tiene tiempo. Reformer · Mat · Sin equipamiento · Tercera edad.',
  bookBg: 'bg-[#D4A24A]',
  bookSpine: 'bg-[#A87E36]'
}, {
  id: 4,
  emoji: '🔧',
  title: 'Reformer + Props',
  desc: 'Cómo integrar bandas elásticas, pesas livianas y aro de Pilates a tus sesiones para multiplicar el estímulo sin cambiar el método.',
  bookBg: 'bg-[#3D5A6C]',
  bookSpine: 'bg-[#2A4150]'
}, {
  id: 5,
  emoji: '🛡️',
  title: 'Checklist de Seguridad y Lesiones',
  desc: 'Guía rápida de decisión clínica. Semáforo de dolor, patologías frecuentes y protocolo de derivación para no cometer errores con la salud de tus alumnos.',
  bookBg: 'bg-[#8B3A4E]',
  bookSpine: 'bg-[#6B2C3C]'
}, {
  id: 6,
  emoji: '🦴',
  title: 'Anatomía Visual: Músculos en Acción',
  desc: 'Mapa muscular de los 20 ejercicios más usados en Reformer y Mat. Sabe exactamente qué músculo trabaja en cada ejercicio y da el cue correcto en segundos.',
  bookBg: 'bg-[#6B5B8C]',
  bookSpine: 'bg-[#4F4368]'
}];
const Thumbnail = ({
  bonus
}: {
  bonus: Bonus;
}) => <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 drop-shadow-md" data-id="element-27">
    {/* Spine */}
    <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${bonus.bookSpine} rounded-l-lg`} data-id="element-28" />
    {/* Cover */}
    <div className={`absolute left-1.5 top-0 right-0 bottom-0 ${bonus.bookBg} rounded-r-lg flex flex-col items-center justify-center text-white p-1`} data-id="element-29">
      <div className="text-xl sm:text-2xl" data-id="element-30">{bonus.emoji}</div>
      <div className="text-[7px] sm:text-[8px] tracking-[0.15em] font-semibold opacity-90 mt-0.5" data-id="element-31">
        PILATES 360™
      </div>
    </div>
  </div>;
export const Bonuses = () => {
  return <section className="py-16 sm:py-20 bg-brand-cream" data-id="element-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-33">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-34">
          <h2 className="flex items-center justify-center gap-3 text-4xl sm:text-5xl font-serif font-bold text-brand-charcoal mb-4 leading-tight" data-id="element-35">
            <span aria-hidden="true" data-id="element-36">🎁</span> 6 Bonos Exclusivos
          </h2>
          <p className="text-lg sm:text-xl text-gray-500" data-id="element-37">
            Valorados en{' '}
            <span className="text-brand-terracotta line-through" data-id="element-38">$114 USD</span>{' '}
            — Incluidos <span className="text-green-600 font-bold" data-id="element-39">GRATIS</span>
          </p>
        </div>

        {/* Bonus List */}
        <div className="space-y-4 sm:space-y-5 mb-14" data-id="element-40">
          {bonuses.map((bonus, i) => <motion.article key={bonus.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.06
        }} className="bg-white rounded-2xl shadow-sm border border-brand-sand p-4 sm:p-5 flex items-center gap-4 sm:gap-5" data-id="element-41">
              {/* Number Badge */}
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-brand-terracotta text-white font-serif font-bold text-lg flex items-center justify-center flex-shrink-0" data-id="element-42">
                {bonus.id}
              </div>

              {/* Thumbnail */}
              <Thumbnail bonus={bonus} data-id="element-43" />

              {/* Text */}
              <div className="flex-1 min-w-0" data-id="element-44">
                <h3 className="text-base sm:text-xl font-serif font-bold text-brand-charcoal leading-snug mb-1" data-id="element-45">
                  {bonus.title}
                </h3>
                <div className="flex items-center gap-2 text-sm sm:text-base font-bold" data-id="element-46">
                  <span className="text-brand-terracotta line-through" data-id="element-47">
                    $19
                  </span>
                  <span className="text-green-600" data-id="element-48">GRATIS</span>
                </div>
              </div>
            </motion.article>)}
        </div>

        {/* Mid CTA (extra strategic button) */}
        <div className="max-w-4xl mx-auto bg-brand-terracotta rounded-3xl p-8 md:p-12 text-center shadow-2xl relative overflow-hidden" data-id="element-49">
          <div className="absolute inset-0 bg-black/10" data-id="element-50" />
          <div className="relative z-10" data-id="element-51">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-6" data-id="element-52">
              🔥 Asegura tu lugar antes de que termine la oferta
            </h3>
            <ScrollButton className="w-full sm:w-auto bg-white text-brand-terracotta hover:bg-brand-sand text-lg font-bold py-4 px-8 rounded-xl shadow-lg transition-all transform hover:-translate-y-1" data-id="element-53">
              QUIERO ACCEDER AL PACK COMPLETO AHORA →
            </ScrollButton>
          </div>
        </div>
      </div>
    </section>;
};